import { CollectionPaginationTriggersProp, CmsFilter, CmsFilterOperator } from 'types/cms';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useListCollectionItems } from 'client/queries/cms/list-collection-items';
import { isVectorSearch } from 'common/utils/cms/vector-search';
import { usePaginationTriggers } from './use-pagination-triggers';
import { FilterReducerAction, useFilterReducer, useFilterTriggers } from './use-filter-triggers';

type UseDataParams = {
	collectionId: string;
	pageSize: number;
	paginationTriggers?: CollectionPaginationTriggersProp;
	collectionFilters?: CmsFilter[];
};

export const useData = ({ collectionId, pageSize, paginationTriggers, collectionFilters = [] }: UseDataParams) => {
	const [filters, _setFilters] = useFilterReducer(collectionFilters);
	const [page, setPage] = useState(0);
	const setFilters = useCallback(
		(newFilters: FilterReducerAction) => {
			_setFilters(newFilters);
			setPage(0);
		},
		[_setFilters]
	);

	useFilterTriggers({ filters: collectionFilters, setFilters });

	/**
	 * Precedence:
	 * - default only at start (no search, no custom)
	 * - search replaces default; can AND with custom
	 * - custom replaces default; can AND with keyword search FIND
	 */
	const filterConditions = useMemo(() => {
		const { filter, search, defaultFilter } = filters;
		const activeFilter = filter ?? (search ? null : defaultFilter);
		const searchOperator = search?.operator || CmsFilterOperator.Contains;
		return [
			search &&
				!isVectorSearch(search) &&
				search.field && {
					type: 'FIND' as const,
					field: search.field,
					value: search.value,
					operator: searchOperator,
				},
			activeFilter && {
				field: activeFilter.field,
				value: activeFilter.value,
				operator: activeFilter.operator,
			},
		].filter(Boolean) as NonNullable<Parameters<typeof useListCollectionItems>[0]['filter']>[];
	}, [filters]);

	const vectorSearch = isVectorSearch(filters.search ?? {}) ? filters.search : null;

	const items = useListCollectionItems({
		collectionId,
		pageSize,
		page,
		...(filterConditions.length ? { filter: { operator: 'AND', conditions: filterConditions } } : null),
		...(vectorSearch
			? { vectorQuery: vectorSearch.value, vectorMinimumScore: vectorSearch.vectorMinimumScore }
			: { orderBy: 'position', orderDir: 'asc' }),
	});
	const { hasNextPage } = items;

	const fetchNextPage = useCallback(() => {
		if (hasNextPage) {
			setPage(currentPage => currentPage + 1);
		}
	}, [hasNextPage]);

	const fetchPreviousPage = useCallback(() => {
		setPage(currentPage => Math.max(currentPage - 1, 0));
	}, []);

	usePaginationTriggers({
		triggers: paginationTriggers,
		fetchNextPage,
		fetchPreviousPage,
		hasNextPage: items.hasNextPage,
		hasPreviousPage: items.hasPreviousPage,
	});

	const pageCurrent = page;
	const [currentItems, setCurrentItems] = useState(items.data?.pages.findLast(p => p.page === pageCurrent));

	useEffect(() => {
		if (items.data && items.isSuccess && !items.isFetching) {
			setCurrentItems(items.data.pages.findLast(p => p.page === pageCurrent));
		}
	}, [items.data, items.isSuccess, items.isFetching, pageCurrent]);

	const itemsMax = currentItems?.total ?? 0;
	const pageMax = Math.max(0, Math.ceil(itemsMax / pageSize) - 1);

	return {
		collectionId,
		items: currentItems,
		isLoading: items.isLoading,
		hasNext: items.hasNextPage,
		hasPrev: items.hasPreviousPage,
		pageCurrent,
		pageMax,
		itemsMax,
	};
};
